import { useState, useEffect, useRef } from 'react';
import { ChevronsUp } from 'lucide-react';
import type { TabType } from '../App';

interface ScrollToTopButtonProps {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  activeTab: TabType;
}

export function ScrollToTopButton({ scrollContainerRef, activeTab }: ScrollToTopButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const lastScrollY = useRef(0);
  const scrollUpStartTime = useRef<number | null>(null);
  const showButtonTimeout = useRef<number | null>(null);
  const hideButtonTimeout = useRef<number | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    
    if (!container || !isMobile || activeTab !== 'maps') {
      setIsVisible(false);
      scrollUpStartTime.current = null;
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = container.scrollTop;
          const isScrollingUp = currentScrollY < lastScrollY.current;
          const hasScrolledDown = currentScrollY > 300;
          
          // Check if user is within bottom 30px of scroll container
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;
          const isNearBottom = currentScrollY + clientHeight >= scrollHeight - 30;

          if (showButtonTimeout.current !== null) {
            clearTimeout(showButtonTimeout.current);
            showButtonTimeout.current = null;
          }
          
          if (hideButtonTimeout.current !== null) {
            clearTimeout(hideButtonTimeout.current);
            hideButtonTimeout.current = null;
          }

          if (isScrollingUp && hasScrolledDown && !isNearBottom) {
            if (scrollUpStartTime.current === null) {
              scrollUpStartTime.current = performance.now();
            }

            const elapsed = performance.now() - scrollUpStartTime.current;
            
            if (elapsed >= 400) {
              setIsVisible(true);
              // Auto-hide after 1.5 seconds of no scrolling
              hideButtonTimeout.current = window.setTimeout(() => {
                setIsVisible(false);
                scrollUpStartTime.current = null;
                hideButtonTimeout.current = null;
              }, 1500);
            } else {
              const remainingTime = 400 - elapsed;
              showButtonTimeout.current = window.setTimeout(() => {
                setIsVisible(true);
                // Auto-hide after 1 seconds of no scrolling
                hideButtonTimeout.current = window.setTimeout(() => {
                  setIsVisible(false);
                  scrollUpStartTime.current = null;
                  hideButtonTimeout.current = null;
                }, 1500);
                showButtonTimeout.current = null;
              }, remainingTime);
            }
          } else {
            setIsVisible(false);
            scrollUpStartTime.current = null;
          }

          lastScrollY.current = currentScrollY;
          
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (showButtonTimeout.current !== null) {
        clearTimeout(showButtonTimeout.current);
      }
      if (hideButtonTimeout.current !== null) {
        clearTimeout(hideButtonTimeout.current);
      }
    };
  }, [scrollContainerRef, isMobile, activeTab]);

  const scrollToTop = () => {
    // Reset scroll tracking to prevent scroll handler from showing button
    scrollUpStartTime.current = null;
    
    // Clear any pending show timeout
    if (showButtonTimeout.current !== null) {
      clearTimeout(showButtonTimeout.current);
      showButtonTimeout.current = null;
    }
    
    // Clear any pending hide timeout
    if (hideButtonTimeout.current !== null) {
      clearTimeout(hideButtonTimeout.current);
      hideButtonTimeout.current = null;
    }
    
    // Scroll to top with 200ms smooth animation
    const container = scrollContainerRef.current;
    if (container) {
      const startScroll = container.scrollTop;
      const duration = 200;
      const startTime = performance.now();
      
      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        
        container.scrollTop = startScroll - (startScroll * easedProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    }
    
    // Hide button immediately
    setIsVisible(false);
  };

  if (!isMobile || activeTab !== 'maps') return null;

  return (
    <button
      onClick={scrollToTop}
      style={{
        background: 'linear-gradient(to bottom, rgba(0, 100, 190, 0.9), rgba(0, 55, 110, 0.9))'
      }}
      className={`absolute bottom-0 left-1/2 -translate-x-1/2 z-30 w-14 h-7 rounded-t-[8px] flex items-center justify-center shadow-[0_4px_10px_rgba(0,0,0,0.2)] border border-[var(--accent-blue)] hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent transition-opacity duration-150 ${
        isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      aria-label="Scroll to top"
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
    >
      <ChevronsUp className="size-5 text-white" strokeWidth={2.5} />
    </button>
  );
}